import React from 'react';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { useRelatedPersons } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/useRelatedPersons.ts';
import { RelatedPersonRowItem } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/RelatedPersonRowItem.tsx';
import { AddRelatedPersonDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/AddRelatedPersonDrawer/AddRelatedPersonDrawer.tsx';
import { getRelationLabelByTypeAndPersonType } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EditRelatedPersonDrawer/utils/relatedPeopleLabel.ts';
import { PersonType } from '@dealstack/shared/lib/generated';

interface Props {
	hasEditorPermission: boolean;
}

export const RelatedPersonsSection = ({ hasEditorPermission }: Props) => {
	const tCapManage = useTranslationCapManage();
	const {
		relatedPersons,
		dealId,
		isAddDrawerOpen,
		openAddDrawer,
		closeAddDrawer,
		rowToDelete,
		setRowToDelete,
		confirmRemoveRelatedPerson,
		removeLoading,
		removeSuccess,
		invalidateRelatedPeople,
	} = useRelatedPersons();

	if (!relatedPersons.length) {
		return null;
	}

	return (
		<>
			<EditableInfoContainer
				title={tCapManage('basic_details.related_persons.title')}
				isEdit={false}
				hasEditorPermission={hasEditorPermission}
				gridColumns={4}
				useGrid={true}
				childrenPadding="0.5rem"
				onAdd={openAddDrawer}
			>
				{relatedPersons.map((row) => (
					<RelatedPersonRowItem
						key={row.relationId}
						row={row}
						dealId={dealId}
						// An OTHER row's badge shows the specific role the admin or
						// investor typed; the generic label is the fallback.
						typeLabel={
							row.customRelationType ??
							getRelationLabelByTypeAndPersonType(
								tCapManage,
								row.relationType,
								PersonType.LEGAL
							)
						}
						hasEditPermission={hasEditorPermission}
						onDelete={() => setRowToDelete(row)}
					/>
				))}
			</EditableInfoContainer>
			{hasEditorPermission && (
				<>
					<AddRelatedPersonDrawer
						opened={isAddDrawerOpen}
						hasPermission={hasEditorPermission}
						onClose={closeAddDrawer}
						onSuccess={invalidateRelatedPeople}
					/>

					<ConfirmDeleteDrawer
						opened={!!rowToDelete}
						onClose={() => setRowToDelete(null)}
						title={tCapManage('delete.related_person.drawer.title')}
						inputLabel={tCapManage('delete.related_person.drawer.input_label')}
						inputPlaceholder={tCapManage(
							'delete.related_person.drawer.input_placeholder'
						)}
						confirmDeleteText={rowToDelete?.displayName || 'Related person'}
						wordMismatchErrorMessage={tCapManage(
							'delete.related_person.drawer.word_mismatch_error'
						)}
						deleteBtnText={tCapManage('delete.related_person.drawer.confirm_delete')}
						cancelBtnText={tCapManage('delete.related_person.drawer.cancel')}
						handleDelete={confirmRemoveRelatedPerson}
						deleteLoading={removeLoading}
						deleteSuccess={removeSuccess}
					/>
				</>
			)}
		</>
	);
};
